import React, { Component, ReactNode, ErrorInfo } from 'react';
import { captureError } from '../../lib/datahub/errorLogging/captureError';

interface Props {
  moduleName: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Module-level Error Boundary
 *
 * Wraps an individual module's route tree so a render crash in one module
 * does not take down the entire application. Navigation, the global shell,
 * and all other modules remain fully functional when one module throws.
 *
 * Features:
 * - Logs to the DataHub error tracking system with module context
 * - Shows a recovery UI with module name, "Try Again", and "Go to Dashboard"
 * - Auto-reloads on ChunkLoadErrors (stale cache after deployment)
 * - Auto-reloads once on a poisoned Firestore AsyncQueue (b815, #3775) — "Try
 *   Again" cannot clear that state, only a fresh queue can
 * - Falls through to the global ErrorBoundary as a final safety net
 */
class ModuleErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { moduleName } = this.props;

    // Handle chunk load errors (stale cache after deployment) — auto-reload once
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Unexpected token');

    if (isChunkError) {
      const key = `chunk_error_reload_${moduleName}`;
      const lastReload = sessionStorage.getItem(key);
      const now = Date.now();

      if (!lastReload || now - parseInt(lastReload, 10) > 60000) {
        sessionStorage.setItem(key, now.toString());
        window.location.reload();
        return;
      }
    }

    // Poisoned Firestore AsyncQueue (#3775) — auto-reload once.
    //
    // When any operation enqueued on Firestore's AsyncQueue rejects, the SDK
    // stores it and every SUBSEQUENT enqueue throws `INTERNAL ASSERTION FAILED:
    // Unexpected state (ID: b815)`. One `db` instance serves the whole app, so
    // the tab is dead for every Firestore-backed page until it is reloaded.
    //
    // "Try Again" CANNOT fix this: handleReset only clears React state, so the
    // remount re-subscribes, re-enqueues, and throws again immediately — an
    // unbreakable loop. A reload is the only recovery, because it builds a fresh
    // AsyncQueue.
    //
    // Switching this app to `memoryLocalCache()` removed the only trigger we
    // have ever observed (losing the IndexedDB lease). It did NOT make b815
    // unreachable — the assertion is generic, and other rejections (watch-stream
    // handling, the auth-token-refresh listener, internal hardAsserts) can still
    // poison the queue. This branch is the recovery half: whatever poisons it,
    // one bad promise must not brick the tab for the rest of the session.
    const isPoisonedQueue =
      error.message?.includes('INTERNAL ASSERTION FAILED') &&
      error.message?.includes('ID: b815');

    if (isPoisonedQueue) {
      // A LATCH, not a time window — and GLOBAL, not per-module.
      //
      // Global because the queue is app-wide: a per-module key would grant a
      // fresh reload for every module the user then navigated to.
      //
      // A latch because the chunk-error pattern above is wrong for this failure.
      // There, a rolling window is correct: each deploy is a genuinely new event
      // and should earn a new reload. Here it is not. If something re-poisons the
      // queue slightly more than the window after each reload, a rolling window
      // re-arms every time and the tab reloads forever at that cadence, throwing
      // away in-progress form state on every pass. Once per tab session, then the
      // UI — sessionStorage survives reload in the same tab, which is exactly the
      // scope we want, and a genuinely new tab gets a fresh allowance.
      const key = 'firestore_b815_reloaded';

      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        // Report before reloading. This is best-effort — the reload may cut the
        // in-flight call — but it is not the durable record: the SDK logs b815
        // to the console on every failed enqueue and consoleAndNetworkCapture
        // reports that independently of this boundary. Losing this one is fine.
        captureError({
          error,
          capturePoint: 'error_boundary',
          severity: 'critical',
          category: 'react_render',
          componentName: `ModuleErrorBoundary(${moduleName})`,
          additionalContext: { moduleName, recovery: 'auto_reload_b815' },
        });
        window.location.reload();
        return;
      }
      // We already spent this tab's reload and b815 came back — reloading again
      // clearly is not the answer, so fall through to the recovery UI. Something
      // re-poisons the queue after a fresh start, which is a genuinely different
      // bug and must stay visible rather than be papered over by a reload loop.
    }

    // Log to DataHub error tracking with module context
    captureError({
      error,
      capturePoint: 'error_boundary',
      severity: 'critical',
      category: 'react_render',
      componentName: `ModuleErrorBoundary(${moduleName})`,
      additionalContext: {
        moduleName,
        componentStack: errorInfo.componentStack,
      },
    });

    console.error(`[ModuleErrorBoundary] Crash in module "${moduleName}":`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { hasError, error } = this.state;
    const { children, moduleName } = this.props;

    if (!hasError) {
      return children;
    }

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-md border border-slate-200 p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="h-6 w-6 text-amber-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {moduleName} module encountered an error
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                This module crashed, but the rest of the app is still running. You can try
                again or navigate to another section.
              </p>
            </div>
          </div>

          {error && process.env.NODE_ENV === 'development' && (
            <div className="mb-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Error Details (Development Only)
              </p>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-xs text-red-800 font-mono whitespace-pre-wrap break-words">
                  {error.toString()}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-navy-600 text-white text-sm font-medium rounded-md hover:bg-navy-700 transition-colors"
            >
              Try Again
            </button>
            <a
              href="/"
              className="px-4 py-2 bg-white text-slate-700 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            >
              Go to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export function ModuleErrorBoundary({ moduleName, children }: Props) {
  return (
    <ModuleErrorBoundaryClass moduleName={moduleName}>
      {children}
    </ModuleErrorBoundaryClass>
  );
}
